import React, { useState } from 'react';
import { MapPin, Phone, Mail, AlertTriangle, Users, Plane, ShieldAlert, CheckCircleIcon, XIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export function TravelersPage() {
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const showToast = (message: string) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(null), 3000);
  };
  const travelers = [{
    id: 1,
    name: 'Sarah Connor',
    location: 'London, UK',
    status: 'Safe',
    lastUpdate: '10 mins ago',
    email: 'sarah@acme.com',
    phone: '+1 555-0123'
  }, {
    id: 2,
    name: 'John Smith',
    location: 'New York, USA',
    status: 'In Transit',
    lastUpdate: '25 mins ago',
    email: 'john@acme.com',
    phone: '+1 555-0124'
  }, {
    id: 3,
    name: 'Emily Chen',
    location: 'Tokyo, Japan',
    status: 'Safe',
    lastUpdate: '1 hour ago',
    email: 'emily@acme.com',
    phone: '+1 555-0125'
  }, {
    id: 4,
    name: 'Michael Brown',
    location: 'Berlin, Germany',
    status: 'Risk Zone',
    lastUpdate: '5 mins ago',
    email: 'michael@acme.com',
    phone: '+1 555-0126'
  }, {
    id: 5,
    name: 'David Wilson',
    location: 'Paris, France',
    status: 'Safe',
    lastUpdate: '2 hours ago',
    email: 'david@acme.com',
    phone: '+1 555-0127'
  }, {
    id: 6,
    name: 'Lisa Wang',
    location: 'Singapore',
    status: 'In Transit',
    lastUpdate: '15 mins ago',
    email: 'lisa@acme.com',
    phone: '+1 555-0128'
  }];
  const stats = [{
    label: 'Total Travelers',
    value: '142',
    icon: Users,
    color: 'bg-blue-50 text-blue-600'
  }, {
    label: 'In Transit',
    value: '34',
    icon: Plane,
    color: 'bg-amber-50 text-amber-600'
  }, {
    label: 'Risk Zone',
    value: '3',
    icon: ShieldAlert,
    color: 'bg-red-50 text-red-600'
  }];
  return <div className="space-y-6" data-id="element-3048">
      {/* Toast */}
      {toastMessage && <div className="fixed top-20 right-6 z-50 animate-in slide-in-from-top-2" data-id="element-3049">
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg p-4 shadow-lg flex items-center gap-3" data-id="element-3050">
            <CheckCircleIcon className="h-5 w-5 text-emerald-600 flex-shrink-0" data-id="element-3051" />
            <p className="font-medium text-sm" data-id="element-3052">{toastMessage}</p>
            <button onClick={() => setToastMessage(null)} className="ml-2 text-emerald-500 hover:text-emerald-700" data-id="element-3053">
              <XIcon className="h-4 w-4" data-id="element-3054" />
            </button>
          </div>
        </div>}

      <div className="flex items-center justify-between" data-id="element-3055">
        <div data-id="element-3056">
          <h2 className="text-2xl font-bold text-slate-800" data-id="element-3057">
            Traveler Tracking
          </h2>
          <p className="text-slate-500" data-id="element-3058">
            Real-time location and duty of care monitoring.
          </p>
        </div>
        <button onClick={() => showToast('Emergency broadcast sent to all travelers')} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 shadow-sm shadow-red-200" data-id="element-3059">
          Emergency Broadcast
        </button>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-id="element-3060">
        {stats.map(stat => <div key={stat.label} className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-id="element-3061">
            <div className={`rounded-full p-3 ${stat.color}`} data-id="element-3062">
              <stat.icon className="h-6 w-6" data-id="element-3063" />
            </div>
            <div data-id="element-3064">
              <p className="text-2xl font-bold text-slate-900" data-id="element-3065">{stat.value}</p>
              <p className="text-sm text-slate-500" data-id="element-3066">{stat.label}</p>
            </div>
          </div>)}
      </div>

      {/* Map Placeholder */}
      <div className="h-96 w-full rounded-xl border border-slate-200 bg-slate-100 flex items-center justify-center relative overflow-hidden shadow-inner" data-id="element-3067">
        <div className="absolute inset-0 bg-[url('https://upload.wikimedia.org/wikipedia/commons/e/ec/World_map_blank_without_borders.svg')] bg-cover opacity-10 grayscale" data-id="element-3068"></div>
        <div className="relative z-10 text-center bg-white/80 backdrop-blur-sm p-6 rounded-2xl border border-slate-200 shadow-lg" data-id="element-3069">
          <MapPin className="mx-auto h-12 w-12 text-blue-500 mb-2" data-id="element-3070" />
          <p className="text-lg font-bold text-slate-800" data-id="element-3071">
            Global Tracking Map
          </p>
          <p className="text-sm text-slate-500" data-id="element-3072">
            Live location data updates every 60s
          </p>
          <button onClick={() => showToast('Full screen map view coming soon')} className="mt-4 text-sm font-medium text-blue-600 hover:underline" data-id="element-3073">
            View Full Screen Map
          </button>
        </div>
      </div>

      {/* Travelers List */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" data-id="element-3074">
        {travelers.map(traveler => <div key={traveler.id} className={`rounded-xl border p-6 shadow-sm transition-all hover:shadow-md ${traveler.status === 'Risk Zone' ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-white'}`} data-id="element-3075">
            <div className="flex items-start justify-between" data-id="element-3076">
              <div className="flex items-center gap-3" data-id="element-3077">
                <div className="h-10 w-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-bold border-2 border-white shadow-sm" data-id="element-3078">
                  {traveler.name.charAt(0)}
                </div>
                <div data-id="element-3079">
                  <h3 className="font-semibold text-slate-900" data-id="element-3080">
                    {traveler.name}
                  </h3>
                  <div className="flex items-center text-xs text-slate-500" data-id="element-3081">
                    <MapPin className="mr-1 h-3 w-3" data-id="element-3082" />
                    {traveler.location}
                  </div>
                </div>
              </div>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${traveler.status === 'Risk Zone' ? 'bg-red-100 text-red-800' : traveler.status === 'In Transit' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`} data-id="element-3083">
                {traveler.status}
              </span>
            </div>

            <div className="mt-4 space-y-2" data-id="element-3084">
              <div className="flex items-center text-sm text-slate-600" data-id="element-3085">
                <Mail className="mr-2 h-4 w-4 text-slate-400" data-id="element-3086" />
                {traveler.email}
              </div>
              <div className="flex items-center text-sm text-slate-600" data-id="element-3087">
                <Phone className="mr-2 h-4 w-4 text-slate-400" data-id="element-3088" />
                {traveler.phone}
              </div>
            </div>

            {traveler.status === 'Risk Zone' && <div className="mt-4 flex items-center gap-2 text-sm text-red-700 bg-red-100/50 p-2 rounded-lg" data-id="element-3089">
                <AlertTriangle className="h-4 w-4" data-id="element-3090" />
                <span data-id="element-3091">Severe weather warning in region</span>
              </div>}

            <div className="mt-4 border-t border-slate-100 pt-4 flex justify-between items-center" data-id="element-3092">
              <span className="text-xs text-slate-400" data-id="element-3093">
                Updated {traveler.lastUpdate}
              </span>
              <Link to="/trips" className="text-sm font-medium text-blue-600 hover:underline" data-id="element-3094">
                View Itinerary
              </Link>
            </div>
          </div>)}
      </div>
    </div>;
}